import { Button } from "@legal-500/legal500-design-system";
import { useEffect, useMemo } from "react";
import type { ContentStatus } from "@/api/profiles";
import styles from "./SaveActions.module.css";

export type SaveActionsStatus = ContentStatus;

export type SaveActionsLabels = {
  saveDraft: string;
  publish: string;
  updatePublished: string;
  delete: string;
};

export type SaveActionsProps = {
  /** The current persisted status (from server) — drives the "Publish" vs "Update published" label */
  currentStatus: SaveActionsStatus;
  canPublish: boolean;
  /** True while a save mutation is in flight */
  isSaving: boolean;
  /** Called with the target status when the user clicks a button. Component does NOT persist — caller handles that. */
  onSave: (targetStatus: SaveActionsStatus) => void;
  /** Optional Delete button at the very bottom (edit mode only). If provided, rendered with danger styling. */
  onDelete?: () => void;
  deleteLabel?: string;
  /** i18n: labels can be overridden. Defaults below. */
  labels?: Partial<SaveActionsLabels>;
  /**
   * When false, suppresses the global `⌘/Ctrl + S` shortcut binding for this
   * instance. Defaults to true.
   */
  enableSaveHotkey?: boolean;
  /**
   * Visual layout:
   * - "stacked" (default): vertical, full-width buttons — designed for sidebar.
   * - "inline": horizontal row of intrinsic-width buttons — designed for a
   *   page header / toolbar so the actions are visible above the fold.
   */
  layout?: "stacked" | "inline";
  /**
   * Additional gate that disables all save buttons (and suppresses the ⌘/Ctrl+S
   * hotkey). Delete is intentionally unaffected.
   */
  disabled?: boolean;
};

const DEFAULT_LABELS: SaveActionsLabels = {
  saveDraft: "Save draft",
  publish: "Publish",
  updatePublished: "Update published",
  delete: "Delete",
};

/** Detect macOS so we can render `⌘S` vs `Ctrl+S` in tooltips. */
function isMacPlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform ??
    "";
  return /mac/i.test(platform);
}

/**
 * Status-driven save controls. The pressed button decides the target status —
 * there is no editable status field. Publish is admin-gated via `canPublish`.
 * Mirrors the account-ui `SaveActions` primitive.
 */
export function SaveActions({
  currentStatus,
  canPublish,
  isSaving,
  onSave,
  onDelete,
  deleteLabel,
  labels,
  enableSaveHotkey = true,
  layout = "stacked",
  disabled = false,
}: SaveActionsProps) {
  const merged = { ...DEFAULT_LABELS, ...labels };
  const publishLabel = currentStatus === "published" ? merged.updatePublished : merged.publish;
  const resolvedDeleteLabel = deleteLabel ?? merged.delete;
  const isInline = layout === "inline";
  const buttonFullWidth = !isInline;

  const saveDraftHint = useMemo(() => {
    const combo = isMacPlatform() ? "⌘S" : "Ctrl+S";
    return `${merged.saveDraft} (${combo})`;
  }, [merged.saveDraft]);

  // ⌘/Ctrl + S — trigger Save draft on the active form. Always intercept so
  // the browser's "Save Page" dialog never fires while the user is editing.
  useEffect(() => {
    if (!enableSaveHotkey) return;

    const handler = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key !== "s" && event.key !== "S") return;
      // Ignore browser-reserved combos like ⌘⇧S ("Save As") and ⌘⌥S.
      if (event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (isSaving || disabled) return;
      onSave("draft");
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enableSaveHotkey, isSaving, disabled, onSave]);

  return (
    <div className={`${styles.root}${isInline ? ` ${styles.inline}` : ""}`}>
      <Button
        type="button"
        variant="outlined"
        color="default"
        fullWidth={buttonFullWidth}
        disabled={isSaving || disabled}
        onClick={() => onSave("draft")}
        title={saveDraftHint}
      >
        {merged.saveDraft}
      </Button>
      {canPublish && (
        <Button
          type="button"
          variant="contained"
          color="success"
          fullWidth={buttonFullWidth}
          disabled={isSaving || disabled}
          onClick={() => onSave("published")}
        >
          {publishLabel}
        </Button>
      )}
      {onDelete && (
        <>
          <hr
            className={isInline ? styles.inlineDivider : styles.divider}
            aria-orientation={isInline ? "vertical" : "horizontal"}
          />
          <Button
            type="button"
            variant="contained"
            color="error"
            fullWidth={buttonFullWidth}
            disabled={isSaving}
            onClick={onDelete}
          >
            {resolvedDeleteLabel}
          </Button>
        </>
      )}
    </div>
  );
}

export default SaveActions;
